/**
 * Design-QA route for the Admin page (mirrors PilotBReview, ClusterNReview).
 * Mounts <AdminResult> with mock data so the visual layout can be reviewed
 * without a real Supabase admin session. Lives alongside the other review
 * routes in main.tsx — no auth required, public during design iteration.
 */
import { Container, Screen, Stack } from '@fluent/ui';
import { AdminResult, NumbersSection, UsersListSection, type AdminListState } from './screens/Admin.js';
import './screens/Admin.css';

const baseAt = (daysAgo: number) =>
  new Date(Date.now() - daysAgo * 86_400_000).toISOString();

/** Enough people to see the shape of the list and the funnel. */
const listMock: AdminListState = {
  kind: 'ok',
  data: {
    admin: { email: 'admin@example.com' },
    count: 24,
    paying: 2,
    stats: {
      users_total: 24,
      paying_now: 2,
      joined_today: 1,
      joined_7d: 9,
      active_today: 3,
      active_7d: 11,
      lessons_today: 7,
      lessons_7d: 38,
      funnel: {
        signed_up: 24,
        started_lesson: 17,
        finished_free_skill: 9,
        ever_subscribed: 4,
        paying_now: 2,
      },
    },
    users: [
      ['Miranda Surmanidze', 'm.surmanidze@example.com', 0, 4, 1, null],
      ['Lile', 'lile.abziani@example.com', 1, 2, 0, null],
      ['mar', 'kirvalidze11@example.com', 1, 0, 0, null],
      ['nikoloz bliadze', 'bliadzenikoloz@example.com', 1, 4, 2, null],
      ['luka gordeziani', 'lukagordeziani@example.com', 2, 4, 0, null],
      ['Nia Shioshvili', 'niashioshvili@example.com', 2, 4, 3, 'trialing'],
      ['Elene Beridze', 'elene1792@example.com', 3, 4, 0, null],
      ['Giorgi Gvelesiani', 'giorgigvelesiani@example.com', 62, 0, 0, 'canceled'],
      ['Toka Khozre', 'tokakhozre@example.com', 120, 1, 0, null],
      ['tornike khozrevanidze', 'tkhozrevanidze@example.com', 121, 4, 14, 'active'],
    ].map(([name, email, days, lessons, tests, status], i) => ({
      id: `u${i}`,
      email: email as string,
      display_name: name as string,
      locale: 'ka',
      has_completed_onboarding: true,
      created_at: baseAt(days as number),
      subscription_status: status as 'trialing' | 'active' | 'canceled' | null,
      subscription_cancelled_at: null,
      subscription_period_ends_at: null,
      lessons_completed: lessons as number,
      tests_completed: tests as number,
    })),
  },
};

const mocks = {
  active: {
    found: true as const,
    admin: { email: 'admin@example.com' },
    profile: {
      id: '0dacbffc-4610-4a7f-8dd7-a4cbd8efc879',
      email: 'tornike@example.com',
      display_name: 'Tornike',
      locale: 'ka',
      timezone: 'Asia/Tbilisi',
      daily_goal_minutes: 20,
      has_completed_onboarding: true,
      created_at: baseAt(16),
    },
    progress: [
      { lesson_id: 'lesson-04-polite', completed_at: baseAt(2), accuracy: 1, xp_earned: 25, perfect: true },
      { lesson_id: 'lesson-03-greetings', completed_at: baseAt(3), accuracy: 0.86, xp_earned: 18, perfect: false },
      { lesson_id: 'lesson-02-names', completed_at: baseAt(4), accuracy: 1, xp_earned: 22, perfect: true },
      { lesson_id: 'lesson-01-hello', completed_at: baseAt(5), accuracy: 0.92, xp_earned: 18, perfect: false },
    ],
    exams: [
      { test_number: 2, score: 44, finished_at: baseAt(1) },
      { test_number: 1, score: 41, finished_at: baseAt(2) },
      { test_number: 1, score: 29, finished_at: baseAt(4) },
      { test_number: 81, score: 33, finished_at: baseAt(3) },
    ],
    xpTotal: 83,
    streak: { current_streak: 4, longest_streak: 12, last_active_date: baseAt(0).slice(0, 10), freezes_available: 1 },
    subscriptions: [],
    latestSub: {
      id: 's1',
      provider_subscription_id: 'sub_01kqaj11809s65bw0r6zan12pe',
      provider_customer_id: 'ctm_01k_demo',
      status: 'active' as const,
      trial_ends_at: null,
      current_period_starts_at: baseAt(5),
      current_period_ends_at: baseAt(-25),
      cancelled_at: null,
      last_event_type: 'subscription.updated',
      last_event_at: baseAt(5),
      created_at: baseAt(12),
    },
    firstChargeAt: baseAt(12),
    transactions: [
      { id: 'txn_01kqwjvncbv5wpn9yq7dn4y2g5', status: 'completed', billed_at: baseAt(5), grand_total: '500', currency: 'USD' },
      { id: 'txn_01kqaj0f2t2kwyyf0tj6wyzv04', status: 'completed', billed_at: baseAt(12), grand_total: '0', currency: 'USD' },
    ],
  },
  trialing: {
    found: true as const,
    admin: { email: 'admin@example.com' },
    profile: {
      id: 'e8aa267e-cbf1-4577-9b9f-88560800ec0c',
      email: 'tamari@example.com',
      display_name: 'Tamari',
      locale: 'ka',
      timezone: 'Asia/Tbilisi',
      daily_goal_minutes: 10,
      has_completed_onboarding: true,
      created_at: baseAt(2),
    },
    progress: [
      { lesson_id: 'lesson-01-hello', completed_at: baseAt(1), accuracy: 1, xp_earned: 25, perfect: true },
    ],
    exams: [{ test_number: 1, score: 37, finished_at: baseAt(1) }],
    xpTotal: 25,
    streak: { current_streak: 1, longest_streak: 1, last_active_date: baseAt(1).slice(0, 10), freezes_available: 0 },
    subscriptions: [],
    latestSub: {
      id: 's2',
      provider_subscription_id: 'sub_trialing_demo',
      provider_customer_id: 'ctm_trialing_demo',
      status: 'trialing' as const,
      trial_ends_at: baseAt(-5),
      current_period_starts_at: baseAt(2),
      current_period_ends_at: baseAt(-5),
      cancelled_at: null,
      last_event_type: 'subscription.trialing',
      last_event_at: baseAt(2),
      created_at: baseAt(2),
    },
    firstChargeAt: null,
    transactions: [],
  },
  free: {
    found: true as const,
    admin: { email: 'admin@example.com' },
    profile: {
      id: 'free-user-id',
      email: 'free@example.com',
      display_name: null,
      locale: 'en',
      timezone: 'Europe/Berlin',
      daily_goal_minutes: 5,
      has_completed_onboarding: false,
      created_at: baseAt(60),
    },
    progress: [],
    exams: [],
    xpTotal: 0,
    streak: null,
    subscriptions: [],
    latestSub: null,
    firstChargeAt: null,
    transactions: [],
  },
  expired: {
    found: true as const,
    admin: { email: 'admin@example.com' },
    profile: {
      id: 'expired-user-id',
      email: 'churned@example.com',
      display_name: 'Giorgi',
      locale: 'ka',
      timezone: 'Asia/Tbilisi',
      daily_goal_minutes: 15,
      has_completed_onboarding: true,
      created_at: baseAt(120),
    },
    progress: [
      { lesson_id: 'lesson-04-polite', completed_at: baseAt(40), accuracy: 0.9, xp_earned: 22, perfect: false },
    ],
    exams: [],
    xpTotal: 220,
    streak: { current_streak: 0, longest_streak: 18, last_active_date: baseAt(40).slice(0, 10), freezes_available: 2 },
    subscriptions: [],
    latestSub: {
      id: 's3',
      provider_subscription_id: 'sub_churned_demo',
      provider_customer_id: 'ctm_churned_demo',
      status: 'active' as const,
      trial_ends_at: null,
      current_period_starts_at: baseAt(20),
      current_period_ends_at: baseAt(-10),
      cancelled_at: null,
      last_event_type: 'subscription.updated',
      last_event_at: baseAt(20),
      created_at: baseAt(80),
    },
    firstChargeAt: baseAt(75),
    transactions: [
      { id: 'txn_demo_recent', status: 'completed', billed_at: baseAt(20), grand_total: '500', currency: 'USD' },
      { id: 'txn_demo_first', status: 'completed', billed_at: baseAt(75), grand_total: '500', currency: 'USD' },
    ],
  },
};

export function AdminReview() {
  return (
    <Screen>
      <Container size="xl">
        <Stack gap={8} style={{ paddingBlock: 'var(--space-6, 24px)' }}>
          <SectionHeader>1. Active subscriber, eligible for refund (12d after first charge)</SectionHeader>
          <AdminResult data={mocks.active} onRefund={() => alert('mock refund')} />

          <SectionHeader>1b. The numbers (what the page opens on)</SectionHeader>
          <NumbersSection state={listMock} />
          <UsersListSection state={listMock} filter="" onFilter={() => {}} onPick={() => {}} />

          <SectionHeader>2. Trialing user (no charge yet)</SectionHeader>
          <AdminResult data={mocks.trialing} onRefund={() => alert('mock refund')} />

          <SectionHeader>3. Free user (no subscription)</SectionHeader>
          <AdminResult data={mocks.free} onRefund={() => alert('mock refund')} />

          <SectionHeader>4. Long-time subscriber, outside refund windows</SectionHeader>
          <AdminResult data={mocks.expired} onRefund={() => alert('mock refund')} />
        </Stack>
      </Container>
    </Screen>
  );
}

function SectionHeader({ children }: { children: React.ReactNode }) {
  return (
    <h2
      style={{
        fontSize: '1.1rem',
        fontWeight: 800,
        color: 'var(--color-text-muted)',
        margin: 0,
        paddingTop: 'var(--space-6, 24px)',
        borderTop: '1px dashed var(--color-border)',
      }}
    >
      {children}
    </h2>
  );
}
